import { FallOutlined, PlusOutlined, RiseOutlined } from "@ant-design/icons";
import { Table, Popconfirm, Button, Flex, message, Pagination, Input, Select, Space, DatePicker, Modal, Tag } from "antd";
import { useState, useEffect } from "react";
import { batchUpdateInvestmentIdea, deleteInvestmentIdea, fetchInvestmentIdea, updateInvestmentIdea } from "../../api/ai";
import dayjs from 'dayjs';
import InvestmentIdeaDrawView from "../../components/ai/invest_idea_draw_view";
import { useDebouncedCallback } from "use-debounce";
import ReportContentModalView from "../../components/ai/report_content_modal_view";
import ProofCheckModalView from "../../components/ai/proof_check_modal_view";
import AuditLogsModalView from "../../components/ai/audit_logs_modal_view";
import idealevel1 from '../../assets/idealevel_1.png';
import idealevel2 from '../../assets/idealevel_2.png';
import idealevel3 from '../../assets/idealevel_3.png';

const AIInvestIdeaScreen = () => {
    const [tabData, setTabData] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currIdea, setCurrIdea] = useState(null);
    const [currRetryInfo, setCurrRetryInfo] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchParams, setSearchParams] = useState({
        ticker: undefined,
        createdAtGte: undefined,
        createdAtLt: undefined,
        status: undefined,
        suggestion: undefined,
    });
    const [selectedRows, setSelectedRows] = useState();
    const [reportShow, setReportShow] = useState(false);
    const [proofShow, setProofShow] = useState(false);
    const [currProof, setCurrProof] = useState(null);
    const [currProofTicker, setCurrProofTicker] = useState(null);
    const [editProofOnly, setEditProofOnly] = useState(false);
    const [actionType, setActionType] = useState(null); // 当前操作类型: null, 'submitChange', 'submitExit'
    const [targetStatus, setTargetStatus] = useState(null); // 目标状态
    const [auditLogsShow, setAuditLogsShow] = useState(false); // auditLogs Modal 显示状态
    const [currAuditLogs, setCurrAuditLogs] = useState([]); // 当前的 auditLogs 数据

    const tabCols = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Ticker',
            dataIndex: 'ticker',
            key: 'ticker',
            render: (text, _) => {
                return <strong>{text}</strong>
            }
        },
        {
            title: 'Target Price',
            dataIndex: 'targetPrice',
            key: 'targetPrice',
            render: (text, _) => {
                return <strong style={{fontSize: 12}}>{text}</strong>
            }
        },
        {
            title: 'Target Price Horizon',
            dataIndex: 'targetPriceHorizon',
            key: 'targetPriceHorizon',
        },
        {
            title: 'Signal Price',
            dataIndex: 'signalPrice',
            key: 'signalPrice',
            render: (text, _) => {
                return text ? <strong>${text}</strong> : '-';
            },
        },
        {
            title: 'Locked Performance',
            dataIndex: 'lockedPerformance',
            key: 'lockedPerformance',
            render: (text, _) => {
                if (text) {
                    return <strong>{(text / 100).toFixed(2)}%</strong>;
                }
                return '-';
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text, _) => {
                const statusMap = {
                    0: { label: 'Draft', color: 'default' },
                    1: { label: 'Active', color: 'green' },
                    2: { label: 'Pending Approve', color: 'orange' },
                    3: { label: 'Pending Change', color: 'orange' },
                    4: { label: 'Pending Exit', color: 'orange' },
                    5: { label: 'Exited', color: 'red' },
                };
                const status = statusMap[text] || { label: 'Unknown', color: 'default' };
                return <Tag color={status.color}>{status.label}</Tag>;
            },
        },
        {
            title: 'Country/Area',
            dataIndex: 'country',
            key: 'country',
            render: (text, _) => {
                return <strong>{text}</strong>;
            },
        },
        {
            title: 'GoAI Conviction',
            dataIndex: 'goaiConviction',
            key: 'goaiConviction',
            render: (text, _) => {
                if (text.toLowerCase().includes('alpha')) {
                    return <img src={idealevel1} alt="Level 1" style={{height: 14}} />;
                } else if (text.toLowerCase().includes('high')) {
                    return <img src={idealevel2} alt="Level 2" style={{height: 14}} />;
                } else if (text.toLowerCase().includes('medium')) {
                    return <img src={idealevel3} alt="Level 3" style={{height: 14}} />;
                }
                return '';
            },
        },
        {
            title: 'GoAI Rating',
            dataIndex: 'rating',
            key: 'rating',
        },
        {
            title: 'Tag',
            dataIndex: 'triggerTag',
            key: 'triggerTag',
            render: (text, record) => {
                return <div style={{width: 200}}>
                    {[record?.triggerTag,record?.category].join(',')}
                </div>;
            },
        },
        {
            title: 'CreatedAt',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text, _) => {
                return <label style={{ fontSize: 12, color: '#afafaf' }}>{text}</label>
            }
        },
        {
            title: 'Votes',
            dataIndex: 'votes',
            key: 'votes',
        },
        {
            title: 'Weight',
            dataIndex: 'weight',
            key: 'weight',
        },
        {
            title: 'Suggestion',
            dataIndex: 'suggestion',
            key: 'suggestion',
            render: (text, _) => {
                if (String(text).toLowerCase() === '0') {
                    return <Tag icon={<RiseOutlined />} color="#16D964">Bullish</Tag>
                } else if (String(text).toLowerCase() === '1') {
                    return <Tag icon={<FallOutlined />} color="#FB465D">Bearish</Tag>
                }
                return '';
            },
        },
        {
            title: 'Report',
            dataIndex: 'aiReportTxt',
            key: 'aiReportTxt',
            render: (text, record) => {
                var report = {};
                try {
                    report = JSON.parse(text);
                } catch (error) { }
                const summary = report?.summary || '';
                return (
                    <div>
                        <span style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1
                        }}>
                            {summary}
                        </span>
                        <span onClick={() => checkReportHandle(record)} style={{fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer', color: '#1890ff'}}>
                            {'view more >'}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'Proof',
            dataIndex: 'proof',
            key: 'proof',
            render: (text, record) => {
                const proofData = record?.proof;
                const proofOfWorkList = proofData?.proofOfWork || [];
                const firstProof = proofOfWorkList.length > 0 ? proofOfWorkList[0] : null;
                if(!firstProof) return '';
                const proofText = firstProof?.highlight || firstProof?.content || '';
                return (
                    <div>
                        <span style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1
                        }}>
                            {proofText}
                        </span>
                        <span onClick={() => checkProofHandle(record)} style={{fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer', color: '#1890ff'}}>
                            {'view more >'}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'Idea Log',
            dataIndex: 'auditLogs',
            key: 'auditLogs',
            render: (text, record) => {
                const auditLogs = record?.auditLogs || [];
                if (auditLogs.length === 0) return '';
                return (
                    <span onClick={() => {
                        setCurrIdea(record);
                        setCurrAuditLogs(auditLogs);
                        setAuditLogsShow(true);
                    }} style={{fontSize: 12, cursor: 'pointer', color: '#1890ff'}}>
                        {'check log'}
                    </span>
                )
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            fixed: 'right',
            render: (_, record) => {
                const renderActionButtons = () => {
                    const buttons = [];

                    // 根据状态显示不同的操作按钮
                    switch (record.status) {
                        case 0: // draft
                            buttons.push(
                                <Button key="edit" type='link' size='small' onClick={() => {
                                    setCurrIdea(record);
                                    setCurrRetryInfo(null);
                                    setEditProofOnly(false);
                                    setActionType(null);
                                    setTargetStatus(null);
                                    setOpen(true);
                                }}>
                                    Edit
                                </Button>,
                                <Button key="submit" type='link' size='small' onClick={() => submitApprovalHandle(record)}>
                                    Submit
                                </Button>
                            );
                            break;
                        case 1: // active
                            buttons.push(
                                <Button key="edit" type='link' size='small' onClick={() => {
                                    setCurrIdea(record);
                                    setCurrRetryInfo(null);
                                    setEditProofOnly(false);
                                    setActionType(null);
                                    setTargetStatus(null);
                                    setOpen(true);
                                }}>
                                    Edit
                                </Button>,
                                <Button key="change" type='link' size='small' onClick={() => submitChangeHandle(record)}>
                                    Submit Change
                                </Button>,
                                <Button key="exit" type='link' size='small' onClick={() => submitExitHandle(record)}>
                                    Submit Exit
                                </Button>,
                                <Button key="cancel" type='link' size='small' onClick={() => cancelActiveHandle(record)}>
                                    Cancel
                                </Button>
                            );
                            break;
                        case 2: // pending_approve
                            buttons.push(
                                <Button key="approve" type='link' size='small' onClick={() => approveSubmitHandle(record)}>
                                    Approve
                                </Button>,
                                <Button key="reject" type='link' size='small' danger onClick={() => rejectSubmitHandle(record)}>
                                    Reject
                                </Button>
                            );
                            break;
                        case 3: // pending_change
                            buttons.push(
                                <Button key="approve" type='link' size='small' onClick={() => approveChangeHandle(record)}>
                                    Approve Change
                                </Button>,
                                <Button key="reject" type='link' size='small' danger onClick={() => rejectChangeHandle(record)}>
                                    Reject Change
                                </Button>
                            );
                            break;
                        case 4: // pending_exit
                            buttons.push(
                                <Button key="approve" type='link' size='small' onClick={() => approveExitHandle(record)}>
                                    Approve Exit
                                </Button>,
                                <Button key="reject" type='link' size='small' danger onClick={() => rejectExitHandle(record)}>
                                    Reject Exit
                                </Button>
                            );
                            break;
                        case 5: // exited
                            // 已退出，无操作
                            break;
                    }

                    // 所有状态都可以编辑 proof 和重试
                    buttons.push(
                        <Button key="editProof" type='link' size='small' onClick={() => editProofHandle(record)}>
                            Edit Proof
                        </Button>,
                        <Button key="retry" type='link' size='small' onClick={() => retryHandle(record)}>
                            Retry
                        </Button>
                    );

                    // 草稿状态可以删除
                    if (record.status === 0) {
                        buttons.push(
                            <Popconfirm
                                key="delete"
                                title="Delete"
                                description="Are you sure to delete this item?"
                                onConfirm={() => deleteHandle(record)}
                                okText="Yes"
                                cancelText="No" >
                                <Button danger type='link' size='small' loading={record.isLoading}>Delete</Button>
                            </Popconfirm>
                        );
                    }

                    return buttons;
                };

                return (
                    <Space size='small' wrap>
                        {renderActionButtons()}
                    </Space>
                );
            }
        }
    ];

    useEffect(() => {
        fetchData(pageNum, pageSize, searchParams);
    }, [])

    const fetchData = useDebouncedCallback((page, pagesize, search) => {
        setLoading(true);
        let params = {
            'page.num': page,
            'page.size': pagesize,
            ...search
        };
        fetchInvestmentIdea(params).then(res => {
            if (res.page) {
                setTotal(res.page.total);
            }
            if (res.list) {
                setTabData(res.list.map(item => {
                    return {
                        ...item,
                        isEditing: false,
                        isLoading: false,
                        selectedIds: []
                    };
                }));
            }
        }).finally(() => setLoading(false))
    }, 300)

    const pageChange = (page, size) => {
        setPageNum(page);
        setPageSize(size);
        fetchData(page, size, searchParams);
    }

    const deleteHandle = (record) => {
        setLoading(true);
        deleteInvestmentIdea(record.id).then(res => {
            if (res) {
                message.success('deleted!');
                fetchData(pageNum, pageSize, searchParams);
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    // 状态转换处理函数
    const submitApprovalHandle = (record) => {
        // 0 → 2: 提交审批
        // Action: submit_active
        let versionTag = 'Initiated';
        let versionTitle = '';
        let versionBody = '';
        Modal.confirm({
            title: 'Submit for Approval',
            width: 500,
            content: (
                <div>
                    <p style={{ marginBottom: 16 }}>Please fill in version history information:</p>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 4 }}>Version Tag:</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="select version tag"
                            defaultValue="Initiated"
                            options={[
                                { key: 1, label: 'Initiated', value: 'Initiated' },
                                { key: 2, label: 'Target Price Adjusted', value: 'Target Price Adjusted' },
                                { key: 3, label: 'Stop-Loss Adjusted', value: 'Stop-Loss Adjusted' },
                                { key: 4, label: 'Horizon Extended', value: 'Horizon Extended' },
                                { key: 5, label: 'Horizon Shortened', value: 'Horizon Shortened' },
                                { key: 6, label: 'Conviction Level Changed', value: 'Conviction Level Changed' },
                                { key: 7, label: 'Thesis Paradigm Shift', value: 'Thesis Paradigm Shift' },
                                { key: 8, label: 'Multiple Adjustment', value: 'Multiple Adjustment' },
                                { key: 9, label: 'Closed', value: 'Closed' },
                            ]}
                            onChange={value => {
                                versionTag = value || 'Initiated';
                            }}
                            allowClear
                        />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 4 }}>Version Title:</div>
                        <Input
                            placeholder="e.g., Coverage Initiated"
                            onChange={evt => {
                                versionTitle = evt.target.value;
                            }}
                        />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 4 }}>Version Body:</div>
                        <Input.TextArea
                            placeholder="e.g., Initial Buy"
                            rows={3}
                            onChange={evt => {
                                versionBody = evt.target.value;
                            }}
                        />
                    </div>
                </div>
            ),
            okText: 'Submit',
            cancelText: 'Cancel',
            onOk() {
                let params = {
                    id: record.id,
                    status: 2
                };
                if (versionTag) params.versionTag = versionTag;
                if (versionTitle) params.versionTitle = versionTitle;
                if (versionBody) params.versionBody = versionBody;
                setLoading(true);
                updateInvestmentIdea(params).then(res => {
                    if (res) {
                        message.success('Submitted for approval!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const approveSubmitHandle = (record) => {
        // 2 → 1: 批准激活
        Modal.confirm({
            title: 'Approve Activation',
            content: 'Are you sure to approve this idea?',
            okText: 'Approve',
            cancelText: 'Cancel',
            onOk() {
                setLoading(true);
                updateInvestmentIdea({
                    id: record.id,
                    status: 1
                }).then(res => {
                    if (res) {
                        message.success('Approved!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const rejectSubmitHandle = (record) => {
        // 2 → 0: 拒绝激活
        let reason = '';
        Modal.confirm({
            title: 'Reject Activation',
            content: (
                <div>
                    <p>Please enter the reason:</p>
                    <Input.TextArea
                        id="rejectReason"
                        placeholder="Reason for rejection"
                        rows={3}
                        onChange={evt => {
                            reason = evt.target.value;
                        }}
                    />
                </div>
            ),
            okText: 'Reject',
            cancelText: 'Cancel',
            onOk() {
                setLoading(true);
                updateInvestmentIdea({
                    id: record.id,
                    status: 0,
                    reason: reason
                }).then(res => {
                    if (res) {
                        message.success('Rejected!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const submitChangeHandle = (record) => {
        // 1 → 3: 提交变更审核
        // Action: submit_active_change
        // 可更新字段: targetPrice, aiReportTxt 中的 stop_loss_price, time_horizon_days, summary, recommended_action
        setCurrIdea(record);
        setCurrRetryInfo(null);
        setEditProofOnly(false);
        setActionType('submitChange');
        setTargetStatus(3);
        setOpen(true);
    }

    const approveChangeHandle = (record) => {
        // 3 → 1: 批准变更
        setLoading(true);
        updateInvestmentIdea({
            id: record.id,
            status: 1,
            isReject: false
        }).then(res => {
            if (res) {
                message.success('Change approved!');
                fetchData(pageNum, pageSize, searchParams);
            }
        }).catch(err => {
            message.error(String(err));
        }).finally(() => {
            setLoading(false);
        });
    }

    const rejectChangeHandle = (record) => {
        // 3 → 1: 拒绝变更
        let reason = '';
        Modal.confirm({
            title: 'Reject Change',
            content: (
                <div>
                    <p>Please enter the reason:</p>
                    <Input.TextArea
                        id="rejectChangeReason"
                        placeholder="Reason for rejection"
                        rows={3}
                        onChange={evt => {
                            reason = evt.target.value;
                        }}
                    />
                </div>
            ),
            okText: 'Reject',
            cancelText: 'Cancel',
            onOk() {
                setLoading(true);
                updateInvestmentIdea({
                    id: record.id,
                    status: 1,
                    isReject: true,
                    reason: reason
                }).then(res => {
                    if (res) {
                        message.success('Change rejected!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const submitExitDiscardChangeHandle = (record) => {
        // 3 → 4: 放弃变更并提交退出审核
        // Action: submit_exit_discard_change
        // 需要编辑 exitDetail 字段，包含 reason: bad_news, target_hit, stop_loss, time_expired
        setCurrIdea(record);
        setCurrRetryInfo(null);
        setEditProofOnly(false);
        setActionType('submitExitDiscardChange');
        setTargetStatus(4);
        setOpen(true);
    }

    const submitExitHandle = (record) => {
        // 1 → 4: 提交退出审核
        // Action: submit_active_exit
        // 需要编辑 exitDetail 字段，包含 reason: bad_news, target_hit, stop_loss, time_expired
        setCurrIdea(record);
        setCurrRetryInfo(null);
        setEditProofOnly(false);
        setActionType('submitExit');
        setTargetStatus(4);
        setOpen(true);
    }

    const approveExitHandle = (record) => {
        // 4 → 5: 批准退出，lockedPerformance 由后端自动获取
        Modal.confirm({
            title: 'Approve Exit',
            content: 'Are you sure you want to approve this exit?',
            okText: 'Approve',
            cancelText: 'Cancel',
            onOk() {
                setLoading(true);
                updateInvestmentIdea({
                    id: record.id,
                    status: 5,
                    exitDetail: record.exitDetail,
                }).then(res => {
                    if (res) {
                        message.success('Exit approved!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const rejectExitHandle = (record) => {
        // 4 → 1: 拒绝退出
        let reason = '';
        Modal.confirm({
            title: 'Reject Exit',
            content: (
                <div>
                    <p>Please enter the reason:</p>
                    <Input.TextArea
                        id="rejectExitReason"
                        placeholder="Reason for rejection"
                        rows={3}
                        onChange={evt => {
                            reason = evt.target.value;
                        }}
                    />
                </div>
            ),
            okText: 'Reject',
            cancelText: 'Cancel',
            onOk() {
                setLoading(true);
                updateInvestmentIdea({
                    id: record.id,
                    status: 1,
                    isReject: true,
                    reason: reason
                }).then(res => {
                    if (res) {
                        message.success('Exit rejected!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const cancelActiveHandle = (record) => {
        // 1 → 0: 取消激活
        let reason = '';
        Modal.confirm({
            title: 'Cancel Activation',
            content: (
                <div>
                    <p>Please enter the reason:</p>
                    <Input.TextArea
                        id="cancelReason"
                        placeholder="Reason for cancellation"
                        rows={3}
                        onChange={evt => {
                            reason = evt.target.value;
                        }}
                    />
                </div>
            ),
            okText: 'Cancel',
            cancelText: 'No',
            onOk() {
                setLoading(true);
                updateInvestmentIdea({
                    id: record.id,
                    status: 0,
                    reason: reason
                }).then(res => {
                    if (res) {
                        message.success('Activation cancelled!');
                        fetchData(pageNum, pageSize, searchParams);
                    }
                }).catch(err => {
                    message.error(String(err));
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    }

    const retryHandle = (record) => {
        setCurrIdea(null);
        setCurrRetryInfo(record);
        setOpen(true);
    }

    const addNewInvestIdea = () => {
        setCurrIdea(null);
        setCurrRetryInfo(null);
        setOpen(true);
    }

    const onSearchChange = (tag, value) => {
        let newParams = { ...searchParams };
        if (tag === 'dates') {
            if (value.length == 2) {
                newParams.createdAtGte = `${value[0]}T00:00:00Z`;
                newParams.createdAtLt = `${value[1]}T23:59:59Z`;
            } else {
                newParams.createdAtGte = undefined;
                newParams.createdAtLt = undefined;
            }
        } else {
            newParams[tag] = value;
        }
        setSearchParams(newParams);
        setPageNum(1);
        fetchData(1, pageSize, newParams);
    }

    const deleteIdeasHandle = () => {
        setLoading(true);
        deleteInvestmentIdea(selectedRows.map(item => item.id).join(',')).then(res => {
            if (res) {
                message.success('deleted!');
                setSelectedRows([]);
                fetchData(pageNum, pageSize, searchParams);
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    const batchUpdateStatus = () => {
        let status = undefined;
        let modal = Modal.confirm({
            width: 600,
            closable: false,
            title: 'Active / Inactive Ideas',
            content: (
                <div>
                    <div style={{ marginTop: 32 }} />
                    <Space style={{ justifyContent: 'center', width: '100%' }} wrap>
                        {
                            selectedRows.map((item) => {
                                return <Tag>{item.ticker}</Tag>
                            })
                        }
                    </Space>
                    <div style={{ marginTop: 32 }} />
                    <Flex align='center'>
                        <div style={{ width: 120, textAlign: 'right', marginRight: 10 }}> Status:</div>
                        <Select
                            style={{ width: 300 }}
                            options={[
                                { key: 1, label: 'Active', value: 1 },
                                { key: 2, label: 'Inactive', value: 0 },
                            ]}
                            placeholder='Select Status'
                            onChange={value => {
                                status = value;
                                modal.update({
                                    okButtonProps: {
                                        disabled: status === undefined,
                                    }
                                })
                            }}
                        />
                    </Flex>
                    <div style={{ marginTop: 20 }} />
                </div>
            ),
            okText: 'Confirm',
            okButtonProps: {
                disabled: status === undefined,
            },
            cancelText: 'Cancel',
            onOk: async () => {
                await batchUpdateInvestmentIdea({ ids: selectedRows.map(item => item.id), status: status });
                setSelectedRows([]);
                fetchData(pageNum, pageSize, searchParams);
            }
        })
    }

    const checkReportHandle = (record) => {
        try {
            JSON.parse(record.aiReportTxt);
            setCurrIdea(record);
            setReportShow(true);
        } catch (error) {
            message.warning('No report content!');
        }
    }

    const editProofHandle = (record) => {
        // 编辑 proof of work 模式
        setCurrIdea(record);
        setCurrRetryInfo(null);
        setEditProofOnly(true);
        setOpen(true);
    }

    const checkProofHandle = (record) => {
        if (record.proof) {
            setCurrProof(record.proof);
            setCurrProofTicker(record.ticker);
            setProofShow(true);
        } else {
            message.warning('No proof content!');
        }
    }

    return (
        <div className='main-wrapper'>
            <div className='top-part cnt-item'>
                <Flex justify='space-between'>
                    <Space>
                        <Input
                            placeholder="ticker search"
                            allowClear
                            onChange={evt => onSearchChange('ticker', evt.target.value)}
                        />
                        <Select
                            style={{ width: 160 }}
                            allowClear
                            options={[
                                { key: 1, label: 'Bullish', value: 0 },
                                { key: 2, label: 'Bearish', value: 1 },
                            ]}
                            placeholder='Select Suggestion'
                            onChange={value => onSearchChange('suggestion', value)}
                        />
                        <Select
                            style={{ width: 200 }}
                            allowClear
                            options={[
                                { key: 0, label: 'Draft', value: 0 },
                                { key: 1, label: 'Active', value: 1 },
                                { key: 2, label: 'Pending Approve', value: 2 },
                                { key: 3, label: 'Pending Change', value: 3 },
                                { key: 4, label: 'Pending Exit', value: 4 },
                                { key: 5, label: 'Exited', value: 5 },
                            ]}
                            placeholder='Select Status'
                            onChange={value => onSearchChange('status', value)}
                        />
                        <DatePicker.RangePicker onChange={values => onSearchChange('dates', (values ?? []).map(date => dayjs(date).format('YYYY-MM-DD')))} />
                    </Space>
                    <Space>
                        <Button type='primary' icon={<PlusOutlined />} onClick={addNewInvestIdea}>Create Investment Idea</Button>
                        {
                            (selectedRows && selectedRows.length > 0) &&
                            <>
                                <Button type='primary' onClick={batchUpdateStatus}>Active / Inactive Items</Button>
                                <Popconfirm
                                    title="Delete"
                                    description="Are you sure to delete these items?"
                                    onConfirm={deleteIdeasHandle}
                                    okText="Yes"
                                    cancelText="No" >
                                    <Button type='primary' danger >Delete Items</Button>
                                </Popconfirm>
                            </>
                        }
                    </Space>
                </Flex>
            </div>
            <div className='cnt-item'>
                <Table
                    loading={loading}
                    rowKey={(record) => record.id}
                    rowClassName={'table-row'}
                    size='small'
                    dataSource={tabData}
                    columns={tabCols}
                    pagination={false}
                    scroll={{ x: true }}
                    rowSelection={{
                        selectedRowKeys: (selectedRows ?? []).map(item => item.id),
                        onChange: (keys, rows) => {
                            setSelectedRows(rows);
                        }
                    }}
                    expandable={{ rowExpandable: (record) => false, showExpandColumn: false }} />
            </div>
            <div className='footer-part cnt-item'>
                <Pagination
                    total={total}
                    showTotal={(total) => `total ${total}`}
                    current={pageNum}
                    pageSize={pageSize}
                    showSizeChanger={true}
                    pageSizeOptions={[10, 20, 30, 50]}
                    onChange={(page, size) => pageChange(page, size)}
                />
            </div>

            <InvestmentIdeaDrawView
                open={open}
                defaultIdea={currIdea}
                retryInfo={currRetryInfo}
                editProofOnly={editProofOnly}
                actionType={actionType}
                targetStatus={targetStatus}
                onClose={() => {
                    setOpen(false);
                    setEditProofOnly(false);
                    setActionType(null);
                    setTargetStatus(null);
                }}
                onUpdate={() => {
                    setEditProofOnly(false);
                    setActionType(null);
                    setTargetStatus(null);
                    fetchData(pageNum, pageSize, searchParams);
                }} />

            <ReportContentModalView
                ticker={currIdea?.ticker}
                report={currIdea?.aiReportTxt}
                open={reportShow}
                onClose={() => setReportShow(false)} />

            <ProofCheckModalView
                proof={currProof}
                ticker={currProofTicker}
                open={proofShow}
                onClose={() => setProofShow(false)} />

            <AuditLogsModalView
                open={auditLogsShow}
                auditLogs={currAuditLogs}
                ticker={currIdea?.ticker}
                onClose={() => setAuditLogsShow(false)} />
        </div>
    )
}

export default AIInvestIdeaScreen;